import styles from '@/pages/app/Settings/styles.module.scss'

import InfoIcon from '@/assets/icons/info.svg?react'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import Tooltip from '@/components/Tooltip'
import PermissionTooltip from '@/pages/app/Settings/PermissionTooltip'
import { NoteSharingContent } from '@/types'

// Keep helperText short: Dropdown menus size to `width: max-content`, so long copy widens them.
// Ordered most to least restrictive, matching the other org controls.
const OPTIONS = [
  {
    id: NoteSharingContent.NotesOnly,
    title: 'Notes only',
    helperText: 'Recipients see the summary, never the transcript.',
  },
  {
    id: NoteSharingContent.NotesAndTranscript,
    title: 'Notes and transcript',
    helperText: 'Recipients invited by email may also open the transcript.',
  },
]

// The setting withholds the verbatim transcript. The summary is generated from it and still
// carries resolved speaker names at every tier, so don't let the copy imply otherwise.
const TOOLTIP = 'How much of a shared note recipients can read. Only people invited by email ever see the transcript.'
const SHARING_DISABLED_TOOLTIP =
  'Note sharing is disabled, so nothing is shared and this setting has no effect.'

const getDropdownItems = (current: NoteSharingContent): DropdownItem[] =>
  OPTIONS.map((option) => ({ ...option, selected: option.id === current }))

type Props = {
  content: NoteSharingContent
  isAdmin: boolean
  isSaving: boolean
  /** True when the audience is set to Disabled — nothing is shared, so content is moot. */
  sharingDisabled: boolean
  onSelect: (content: NoteSharingContent) => void
}

/**
 * Org-wide ceiling on how much of a shared meeting note a recipient may read.
 *
 * Items are derived from `content` on every render rather than held in local state, so the
 * dropdown can't drift from the server value and needs no resync effect or manual rollback.
 */
export default function NoteSharingContentSection({
  content,
  isAdmin,
  isSaving,
  sharingDisabled,
  onSelect,
}: Props) {
  const handleSelect = (selectedItems: DropdownItem[]) => {
    if (selectedItems.length === 0) return
    onSelect(selectedItems[0].id as NoteSharingContent)
  }

  return (
    <div className={styles.settingsItem} data-testid="note-sharing-content-setting">
      <div className={styles.toggleLabel}>
        Shared note content
        <Tooltip text={sharingDisabled ? `${TOOLTIP} ${SHARING_DISABLED_TOOLTIP}` : TOOLTIP} placement="top" hideArrow>
          <InfoIcon className={styles.infoIcon} />
        </Tooltip>
      </div>
      <PermissionTooltip>
        <Dropdown
          items={getDropdownItems(content)}
          onSelect={handleSelect}
          placeholder="Select content"
          className={styles.localDataPolicyDropdown}
          disabled={!isAdmin || isSaving || sharingDisabled}
          ariaLabel="Shared note content"
        />
      </PermissionTooltip>
    </div>
  )
}
